import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
// Card components are not used in this component
import { trpc } from '../trpc';
import { ConfirmationDialog } from './ConfirmationDialog';
import { TiersAndPricing, PlanType } from './TiersAndPricing';
import { analytics } from '../lib/analytics';

// PlanType is now imported from TiersAndPricing

interface SubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: string;
  /**
   * If true, the dialog cannot be closed by clicking outside or pressing escape
   * Used to force subscription selection
   */
  forceSubscription?: boolean;
  /**
   * Payment rejection status
   */
  paymentRejected?: boolean;
  /**
   * Days since payment rejection
   */
  daysSinceRejection?: number;
  /**
   * If true, the payment dialog can be dismissed
   * This is only applicable in the first 3 days after rejection
   */
  canDismiss?: boolean;
  /**
   * If true, indicates that the trial has expired
   */
  trialExpired?: boolean;
}

// No debug helpers

// Debugging tools are commented out for production use
// Uncomment if needed for troubleshooting

/*
// Create a more reliable global log store that persists across renders
if (!(window as any).stripeLogsStore) {
  (window as any).stripeLogsStore = [];
}

// Create a global object to store the last error
(window as any).stripeDebug = {
  lastError: null,
  userData: null,
  subscription: null
};

// Store references to mutation functions for direct access
(window as any).stripeMutations = {
  updateSubscription: null,
};

// Add a force subscription function that can be called from the console
(window as any).forceSubscriptionChange = function(planType) {
  if (!planType || !['STARTER', 'PRO', 'POWER'].includes(planType)) {
    console.error('Invalid plan type. Must be STARTER, PRO, or POWER');
    return false;
  }
  
  const userInfo = (window as any).stripeDebug.userData;
  if (!userInfo || !userInfo.stripeCustomerId) {
    console.error('No user data loaded or user has no Stripe customer ID');
    return false;
  }
  
  console.log(`Forcing subscription change to ${planType} with force update flag`);
  
  try {
    // Use the stored mutation function
    const updateFn = (window as any).stripeMutations.updateSubscription;
    if (typeof updateFn !== 'function') {
      console.error('Update subscription function not available yet. Please open the subscription dialog first.');
      return false;
    }
    
    updateFn({ 
      planType: planType, 
      _forceUpdate: true,
      _isFreeTrial: true  // Force this flag to ensure it's handled specially
    });
    
    return 'Request sent - check console for results';
  } catch (error) {
    console.error('Error calling force subscription:', error);
    return false;
  }
};

// Create a global function to view logs
(window as any).viewStripeLogs = function() {
  console.clear();
  console.log("=== STRIPE DEBUG LOGS ===");
  const logs = (window as any).stripeLogsStore || [];
  if (logs.length === 0) {
    console.log("No logs captured yet. Try again after clicking a subscription button.");
    return "No logs captured yet";
  }
  
  logs.forEach((log: any, index: number) => {
    console.log(`${index + 1}. [${log.timestamp.substring(11, 19)}] ${log.message}`);
  });
  
  // Also show current debug state
  console.log("\n=== STRIPE DEBUG STATE ===");
  console.log("User Data:", (window as any).stripeDebug.userData);
  console.log("Subscription:", (window as any).stripeDebug.subscription);
  console.log("Last Error:", (window as any).stripeDebug.lastError);
  
  return `${logs.length} logs displayed above`;
};

// Create a direct global log function that can be called from console
(window as any).stripeLog = function(message: string, data?: any) {
  try {
    let logMessage = message;
    if (data) {
      if (typeof data === 'object') {
        logMessage += " " + JSON.stringify(data);
      } else {
        logMessage += " " + String(data);
      }
    }
    
    // Also log to console
    console.log(`[STRIPE] ${logMessage}`);
    
    // Store in the global log array
    if (!(window as any).stripeLogsStore) {
      (window as any).stripeLogsStore = [];
    }
    
    (window as any).stripeLogsStore.push({
      message: logMessage,
      timestamp: new Date().toISOString()
    });
    
    // Keep only the last 100 logs
    if ((window as any).stripeLogsStore.length > 100) {
      (window as any).stripeLogsStore.shift();
    }
    
    return true;
  } catch (error) {
    console.error("Error logging:", error);
    return false;
  }
};

// Function to add logs from within the component - disabled for production
function addStripeLog(message: string, data?: any) {
  // No-op function that does nothing in production
  return;
}
*/

// Simple no-op function to replace addStripeLog calls
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function addStripeLog(_: string, __?: any) {
  // No-op function that does nothing
  return;
}

export function SubscriptionDialog({
  open,
  onOpenChange,
  currentPlan,
  forceSubscription = false,
  paymentRejected = false,
  daysSinceRejection = 0,
  canDismiss = true,
  trialExpired = false,
}: SubscriptionDialogProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // Get current user to check account status
  const { data: userData } = trpc.users.getCurrentUser.useQuery();

  // Get account status from user plan data
  const { data: userPlan } = trpc.users.getUserPlan.useQuery();

  // Check if user is new (has zero subscriptions) - used for congratulations redirect
  const { data: userStatus } = trpc.users.isNewUser.useQuery(undefined, {
    enabled: open && !!userData?.id, // Only query when dialog is open AND user is loaded
    staleTime: 0, // Always fresh - we need accurate count before Stripe redirect
    refetchOnMount: true,
    retry: 1, // Reduce retries to avoid spam
  });

  // Get active subscription - needed for both plan changes and cancellation date
  const { data: subscription, refetch: refetchSubscription } =
    trpc.stripe.getActiveSubscription.useQuery(undefined, {
      // CRITICAL FIX: Always enable this query regardless of plan type
      // This ensures we always attempt to find subscriptions, especially for FREE_TRIAL users
      enabled: true,
      retry: 3, // Increase retries for more reliability
      staleTime: 10000, // Refresh more frequently (10 seconds)
      refetchInterval: 30000, // Also refetch every 30 seconds
      refetchOnMount: true, // Always refetch when component mounts
      refetchOnWindowFocus: true, // Refetch when window regains focus
    });

  const manageSubscription = trpc.stripe.createPortalSession.useMutation({
    onSuccess: (data: { url?: string }) => {
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(t('subscription.notifications.noPortalUrl'));
        setIsLoading(false);
      }
    },
    onError: (error: Error) => {
      console.error('Portal session error:', error);
      toast.error(t('subscription.notifications.portalFailed'));
      setIsLoading(false);
    },
  });

  // Dedicated mutation for updating billing information
  const updateBillingInfo = trpc.stripe.createPortalSession.useMutation({
    onSuccess: (data: { url?: string }) => {
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(t('subscription.notifications.noPortalUrl'));
        setIsLoading(false);
      }
    },
    onError: (error: Error) => {
      console.error('Update billing info error:', error);
      toast.error(t('subscription.notifications.billingFailed'));
      setIsLoading(false);
    },
  });

  const checkoutSubscription = trpc.stripe.createCheckoutSession.useMutation({
    onSuccess: (data: { url?: string }) => {
      addStripeLog('CHECKOUT SESSION SUCCESS:', {
        hasUrl: !!data?.url,
      });

      if (data && data.url) {
        toast.success(t('subscription.notifications.redirecting'));
        window.location.href = data.url;
      } else {
        toast.error(t('subscription.notifications.noUrl'));
        setIsLoading(false);
      }
    },
    onError: (error: Error) => {
      addStripeLog('CHECKOUT SESSION ERROR:', {
        error: error.message,
      });

      console.error('Checkout session error:', error);
      toast.error(t('subscription.notifications.checkoutError'));
      setIsLoading(false);
    },
  });

  // Add new mutation for direct plan updates
  const updateSubscriptionPlan = trpc.stripe.updateSubscriptionPlan.useMutation(
    {
      onSuccess: () => {
        toast.success(t('subscription.notifications.updateSuccess'));
        setIsLoading(false);
        onOpenChange(false); // Close dialog after successful update
        // Refresh the page to show updated plan
        window.location.reload();
      },
      onError: (error: Error) => {
        console.error('Plan update error:', error);
        toast.error(t('subscription.notifications.updateFailed'));
        setIsLoading(false);
      },
    }
  );

  // We don't need the emergency update anymore as the main flow works correctly

  // Mutation for canceling the account
  const cancelAccount = trpc.users.cancelAccount.useMutation({
    onSuccess: (data: { accountStatus: string; accessUntil?: Date }) => {
      setIsLoading(false);
      onOpenChange(false);

      if (data.accountStatus === 'canceling') {
        // Format date for display
        const endDate = data.accessUntil
          ? new Date(data.accessUntil).toLocaleDateString()
          : 'the end of your billing period';

        toast.success(
          t('subscription.notifications.cancelSuccess', { endDate }),
          { duration: 10000 }
        );
      } else {
        toast.success(t('subscription.notifications.cancelImmediateSuccess'), {
          duration: 5000,
        });
        // Redirect to homepage for immediate cancellations
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    },
    onError: (error: Error) => {
      setIsLoading(false);
      console.error('Account cancellation error:', error);
      toast.error(t('subscription.notifications.cancelFailed'));
    },
  });

  // Mutation for reactivating the account
  const reactivateAccount = trpc.users.reactivateAccount.useMutation({
    onSuccess: () => {
      setIsLoading(false);
      onOpenChange(false);
      toast.success(t('subscription.notifications.reactivated'));
      // Reload the page to update UI
      window.location.reload();
    },
    onError: (error: Error) => {
      setIsLoading(false);
      console.error('Account reactivation error:', error);
      toast.error(t('subscription.notifications.reactivateFailed'));
    },
  });

  const handleSubscribe = (planId: PlanType) => {
    setIsLoading(true);

    try {
      // Store new user status in localStorage for congratulations page redirect
      if (userStatus?.isNewUser) {
        localStorage.setItem('user_is_new_for_congratulations', 'true');
      }

      // Simple debug log if needed in future
      // Log the subscription change attempt
      addStripeLog('SUBSCRIPTION CHANGE ATTEMPT:', {
        from: currentPlan,
        to: planId,
        hasStripeCustomerId: !!userData?.stripeCustomerId,
        isNewUser: userStatus?.isNewUser,
      });

      // CRITICAL: ALL users should use direct update if they have a Stripe customer ID,
      // INCLUDING free trial users! They've already provided payment info during signup.
      // The only time we should use checkout is if they literally have no Stripe account yet.
      const hasStripeCustomerId = !!userData?.stripeCustomerId;

      // FREE_TRIAL users WITH a stripeCustomerId should use direct update
      // FREE_TRIAL users WITHOUT a stripeCustomerId should use checkout
      // Non-FREE_TRIAL users should always use direct update
      const shouldUseDirectUpdate =
        hasStripeCustomerId || currentPlan !== 'FREE_TRIAL';

      // Log the subscription flow decision
      addStripeLog('SUBSCRIPTION FLOW DECISION:', {
        hasStripeCustomerId,
        currentPlan,
        shouldUseDirectUpdate,
        userData: userData
          ? {
              id: userData.id,
              planType: userData.planType,
              hasStripeCustomerId: !!userData.stripeCustomerId,
              hasStripeSubscriptionId: !!userData.stripeSubscriptionId,
            }
          : 'userData not loaded yet',
      });

      if (shouldUseDirectUpdate) {
        // Make sure we actually have the subscription data
        if (!subscription) {
          addStripeLog(
            'No active subscription found - checking fallback options'
          );

          // CRITICAL CHANGE: If user has a Stripe customer ID but no subscription object,
          // we should still try to use direct update
          if (userData?.stripeCustomerId) {
            // Log the state for debugging purposes
            addStripeLog('userData details:', {
              hasStripeCustomerId: !!userData?.stripeCustomerId,
              hasStripeSubscriptionId: !!userData?.stripeSubscriptionId,
              planType: userData?.planType,
            });

            // IMPORTANT: If they have stripeSubscriptionId in user record, ALWAYS use that
            // Doesn't matter if subscription query failed, we know they have one
            if (userData?.stripeSubscriptionId) {
              // Use direct subscription ID for update - NEVER FALLBACK TO CHECKOUT HERE
              updateSubscriptionPlan.mutate(
                {
                  planType: planId,
                  _directSubscriptionId: userData.stripeSubscriptionId,
                },
                {
                  onSuccess: () => {
                    toast.success(
                      t('subscription.notifications.updateSuccess')
                    );
                    setIsLoading(false);
                    onOpenChange(false);
                    // Refresh the page
                    window.location.reload();
                  },
                  onError: (directError: Error) => {
                    console.error('Subscription update failed:', directError);

                    // NEVER FALL BACK TO CHECKOUT - show error instead
                    setIsLoading(false);
                    toast.error(t('subscription.notifications.updateFailed'));
                  },
                }
              );
              return;
            }

            // Use direct update with force update flag - CRITICAL FOR FREE_TRIAL USERS WITH CUSTOMER ID
            // This enables special case handling to create a new subscription using the existing customer ID
            addStripeLog(
              'FREE_TRIAL USER WITH CUSTOMER ID - USING FORCE UPDATE:',
              {
                customerIdExists: !!userData?.stripeCustomerId,
                planType: currentPlan,
                targetPlan: planId,
                stripeCustomerId: userData?.stripeCustomerId
                  ? userData.stripeCustomerId.substring(0, 8) + '...'
                  : null,
              }
            );

            // Add a toast to show the user something is happening
            toast.info(t('subscription.notifications.processing'));

            // For FREE_TRIAL users with customer ID, add _isFreeTrial flag to trigger special handling
            const isFreeTrialUser = currentPlan === 'FREE_TRIAL';

            updateSubscriptionPlan.mutate(
              {
                planType: planId,
                _forceUpdate: true, // This tells the backend to create subscription if needed
                _isFreeTrial: isFreeTrialUser, // Special flag for FREE_TRIAL users
              },
              {
                onSuccess: (data: any) => {
                  addStripeLog('Force update successful:', data);
                  toast.success(t('subscription.notifications.updateSuccess'));
                  setIsLoading(false);
                  onOpenChange(false);
                  // Refresh the page
                  window.location.reload();
                },
                onError: (error: Error) => {
                  console.error('Subscription update failed:', error);
                  setIsLoading(false);

                  // Log error to console
                  console.error('Subscription error:', error);

                  // Log detailed error information
                  addStripeLog('SUBSCRIPTION ERROR DETAILS:', {
                    message: error.message,
                    data: (error as any).data,
                    shape: (error as any).shape,
                  });

                  // If the error suggests using checkout instead, try that
                  if (
                    typeof error.message === 'string' &&
                    (error.message.includes('No active subscription') ||
                      error.message.includes('Could not retrieve subscription'))
                  ) {
                    toast.info(
                      t('subscription.notifications.redirectingCheckout')
                    );
                    setTimeout(() => {
                      // Use congratulations route for new users
                      const successUrl = userStatus?.isNewUser
                        ? `${window.location.origin}/congratulations?subscription=success`
                        : `${window.location.origin}/dashboard?subscription=success`;

                      checkoutSubscription.mutate({
                        planType: planId,
                        successUrl,
                        cancelUrl: `${window.location.origin}/dashboard?subscription=canceled`,
                      });
                    }, 1000);
                    return;
                  }

                  // Show error
                  toast.error(t('subscription.notifications.updateFailed'));
                },
              }
            );
            return;
          }

          // If user doesn't have a customer ID, try refreshing subscription data before giving up
          refetchSubscription().then((result: any) => {
            if (result.data) {
              if (process.env.NODE_ENV === 'development') {
                console.log('Subscription refetched successfully');
              }

              // Now update with the refreshed data
              updateSubscriptionPlan.mutate(
                { planType: planId },
                {
                  onSuccess: () => {
                    toast.success(
                      t('subscription.notifications.updateSuccess')
                    );
                    setIsLoading(false);
                    onOpenChange(false);
                    // Refresh the page
                    window.location.reload();
                  },
                  onError: (error: Error) => {
                    console.error('Plan update error after refetch:', error);
                    setIsLoading(false);
                    toast.error(t('subscription.notifications.updateFailed'));
                  },
                }
              );
            } else {
              // If user has a customer ID, try to create subscription directly
              if (userData?.stripeCustomerId) {
                if (process.env.NODE_ENV === 'development') {
                  console.log(
                    'No subscription after refetch, trying direct update'
                  );
                }

                // Use force update approach
                updateSubscriptionPlan.mutate(
                  {
                    planType: planId,
                    _forceUpdate: true, // Create subscription if needed
                  },
                  {
                    onSuccess: () => {
                      toast.success(
                        t('subscription.notifications.updateSuccess')
                      );
                      setIsLoading(false);
                      onOpenChange(false);
                      window.location.reload();
                    },
                    onError: (error: Error) => {
                      console.error('Subscription update failed:', error);
                      setIsLoading(false);
                      toast.error(t('subscription.notifications.updateFailed'));
                    },
                  }
                );
              } else {
                // Only use checkout for users with no customer ID
                setIsLoading(false);
                toast.info(t('subscription.notifications.redirectingCheckout'));

                // Use congratulations route for new users
                const successUrl = userStatus?.isNewUser
                  ? `${window.location.origin}/congratulations?subscription=success`
                  : `${window.location.origin}/dashboard?subscription=success`;

                checkoutSubscription.mutate({
                  planType: planId,
                  successUrl,
                  cancelUrl: `${window.location.origin}/dashboard?subscription=canceled`,
                });
              }
            }
          });
          return;
        }

        // Use direct update with valid subscription data
        if (process.env.NODE_ENV === 'development') {
          console.log(
            'Using direct update with subscription ID:',
            subscription.stripeSubscriptionId
          );
        }

        updateSubscriptionPlan.mutate(
          { planType: planId },
          {
            onSuccess: () => {
              toast.success(t('subscription.notifications.updateSuccess'));
              setIsLoading(false);
              onOpenChange(false);
              // Refresh the page
              window.location.reload();
            },
            onError: (error: Error) => {
              console.error('Plan update error:', error);
              setIsLoading(false);
              toast.error(t('subscription.notifications.updateFailed'));
            },
          }
        );
      } else {
        // Create a checkout session for new subscriptions or FREE_TRIAL upgrades
        addStripeLog('USING CHECKOUT SESSION for plan:', {
          planType: planId,
          currentPlan,
          isFreeTrialUpgrade: currentPlan === 'FREE_TRIAL',
          hasStripeCustomerId,
        });

        // Important: Show toast to let user know something is happening
        toast.info(t('subscription.notifications.processing'));

        // Use congratulations route for new users, dashboard for existing users
        const successUrl = userStatus?.isNewUser
          ? `${window.location.origin}/congratulations?subscription=success`
          : `${window.location.origin}/dashboard?subscription=success`;

        analytics.track('stripe_checkout_started', { plan: planId });
        checkoutSubscription.mutate({
          planType: planId,
          successUrl,
          cancelUrl: `${window.location.origin}/dashboard?subscription=canceled`,
        });
      }
    } catch (error) {
      console.error('Subscription error caught in handler:', error);

      // Check if this is a TRPC error (more reliable than just checking message)
      if (
        error &&
        typeof error === 'object' &&
        ('message' in error || 'data' in error) &&
        // Either the error message includes fallback OR it's a NOT_FOUND error
        (('message' in error &&
          typeof error.message === 'string' &&
          error.message.includes('fallback to Stripe checkout')) ||
          ('data' in error &&
            error.data &&
            typeof error.data === 'object' &&
            'code' in error.data &&
            error.data.code === 'NOT_FOUND') ||
          // Also detect error with message about subscription not found
          ('message' in error &&
            typeof error.message === 'string' &&
            error.message.includes('No active subscription found')))
      ) {
        console.log(
          'Received fallback to checkout error, redirecting to Stripe checkout'
        );
        toast.info(t('subscription.notifications.redirectingManagement'));

        // Use checkout for this case
        setTimeout(() => {
          // Use congratulations route for new users
          const successUrl = userStatus?.isNewUser
            ? `${window.location.origin}/congratulations?subscription=success`
            : `${window.location.origin}/dashboard?subscription=success`;

          checkoutSubscription.mutate({
            planType: planId,
            successUrl,
            cancelUrl: `${window.location.origin}/dashboard?subscription=canceled`,
          });
        }, 1000);
      } else {
        toast.error(t('subscription.notifications.checkoutError'));
        setIsLoading(false);
      }
    }
  };

  const handleManageSubscription = () => {
    setIsLoading(true);

    try {
      // Open the customer portal to manage subscription
      // Return the user to their current page after managing subscription
      manageSubscription.mutate({
        returnUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
      });
    } catch (error) {
      console.error('Manage subscription error caught in handler:', error);
      toast.error(t('subscription.notifications.portalFailed'));
      setIsLoading(false);
    }
  };

  // Handler specifically for updating billing information
  const handleUpdateBillingInfo = () => {
    setIsLoading(true);

    try {
      // Open the customer portal directly to the payment method update page
      updateBillingInfo.mutate({
        returnUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
        flowType: 'payment_method_update', // Directs to payment methods page
      });
    } catch (error) {
      console.error('Update billing info error caught in handler:', error);
      toast.error(t('subscription.notifications.billingFailed'));
      setIsLoading(false);
    }
  };

  // Handle account cancellation
  const handleCancelAccount = () => {
    setShowCancelConfirmation(true);
  };

  // Confirm account cancellation
  const confirmCancelAccount = () => {
    setShowCancelConfirmation(false);
    setIsLoading(true);

    try {
      cancelAccount.mutate();
    } catch (error) {
      console.error('Error canceling account:', error);
      setIsLoading(false);
      toast.error(t('subscription.notifications.cancelFailed'));
    }
  };

  // Handle account reactivation
  const handleReactivateAccount = () => {
    setIsLoading(true);

    try {
      reactivateAccount.mutate();
    } catch (error) {
      console.error('Error reactivating account:', error);
      setIsLoading(false);
      toast.error(t('subscription.notifications.reactivateFailed'));
    }
  };

  // Helper function to safely calculate days remaining
  const getDaysRemaining = () => {
    // Primary: Get days remaining from userPlan.accessUntil (most accurate)
    if (userPlan?.accessUntil) {
      const endDate = new Date(userPlan.accessUntil);
      const today = new Date();

      // Set both dates to midnight to get accurate day count
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Calculate difference in milliseconds and convert to days
      const differenceMs = endDate.getTime() - today.getTime();
      const daysDifference = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

      return Math.max(0, daysDifference);
    }

    // Fallback 1: Use subscription data if available
    if (subscription?.currentPeriodEnd) {
      const endDate = new Date(subscription.currentPeriodEnd);
      const today = new Date();

      // Set both dates to midnight to get accurate day count
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Calculate difference in milliseconds and convert to days
      const differenceMs = endDate.getTime() - today.getTime();
      const daysDifference = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

      return Math.max(0, daysDifference);
    }

    // Fallback 2: For canceled/canceling accounts with no end date available
    if (
      userPlan?.accountStatus === 'canceled' ||
      userPlan?.accountStatus === 'canceling'
    ) {
      // If they have trialing status or their account is actually on trial, show at least 1 day
      if (
        subscription?.status === 'trialing' ||
        userPlan.planType === 'FREE_TRIAL'
      ) {
        return 1;
      }
    }

    // No end date available
    return null;
  };

  // Get days remaining for display
  const daysRemaining = getDaysRemaining();

  // Debug logging in development mode only
  // Helper function to convert plan type to display name
  const getPlanDisplayName = (planType: string) => {
    switch (planType) {
      case 'STARTER':
        return 'Lite';
      case 'PRO':
        return 'Pro';
      case 'POWER':
        return 'Power';
      case 'FREE_TRIAL':
        return 'Free trial';
      default:
        return (
          planType.charAt(0).toUpperCase() + planType.slice(1).toLowerCase()
        );
    }
  };

  // Helper function to detect scheduled plan changes
  const getScheduledPlanChange = () => {
    if (
      !subscription ||
      !subscription.planType ||
      subscription.planType === currentPlan
    ) {
      return null;
    }

    // If subscription plan type differs from user plan type, there's a scheduled change
    return {
      from: currentPlan,
      to: subscription.planType,
      toDisplayName: getPlanDisplayName(subscription.planType),
      effectiveDate: subscription.currentPeriodEnd,
    };
  };

  const scheduledPlanChange = getScheduledPlanChange();

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Subscription Dialog - Current State:', {
        subscription: subscription
          ? {
              id: subscription.id,
              status: subscription.status,
              planType: subscription.planType,
              currentPeriodEnd: subscription.currentPeriodEnd,
              cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
            }
          : 'No subscription data',
        userPlan: userPlan || 'No user plan data',
        currentPlan,
        scheduledPlanChange,
        daysRemaining,
        accountStatus: userPlan?.accountStatus || 'Unknown status',
        showReactivateButton:
          userPlan?.accountStatus === 'canceling' ||
          userPlan?.accountStatus === 'canceled',
      });
    }
  }, [subscription, userPlan, daysRemaining, currentPlan, scheduledPlanChange]);

  // Custom onOpenChange handler that prevents closing dialog if subscription is forced or payment update is required
  const handleOpenChange = (newOpen: boolean) => {
    // If we're forcing subscription and trying to close the dialog, prevent it
    if (forceSubscription && !newOpen) {
      return;
    }

    // If payment is rejected, check if the dialog can be dismissed
    if (paymentRejected && !canDismiss && !newOpen) {
      return; // Prevent closing
    }

    // Otherwise, allow the change
    onOpenChange(newOpen);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        // Prevent closing on escape or outside click if subscription is forced or payment update is required
        {...({
          onEscapeKeyDown: (e: any) =>
            (forceSubscription || (paymentRejected && !canDismiss)) &&
            e.preventDefault(),
          onPointerDownOutside: (e: any) =>
            (forceSubscription || (paymentRejected && !canDismiss)) &&
            e.preventDefault(),
        } as any)}
      >
        <DialogContent
          className='sm:max-w-[800px] max-h-[90vh] overflow-auto'
          // Remove close button if subscription is forced or payment update is required
          hasCloseButton={
            !forceSubscription && (canDismiss || !paymentRejected)
          }
        >
          <DialogHeader>
            <DialogTitle>
              {paymentRejected
                ? t('subscription.dialog.paymentUpdateTitle')
                : t('subscription.dialog.title')}
            </DialogTitle>
            <DialogDescription>
              {paymentRejected
                ? daysSinceRejection >= 4
                  ? t('subscription.dialog.description.paymentRejectedFinal')
                  : t('subscription.dialog.description.paymentRejected')
                : trialExpired
                  ? t('subscription.dialog.description.trialExpired')
                  : currentPlan === 'FREE_TRIAL'
                    ? t('subscription.dialog.description.freeTrial')
                    : t('subscription.dialog.description.currentPlan', {
                        plan: currentPlan,
                      })}
            </DialogDescription>
          </DialogHeader>

          {/* Trial expired notification */}
          {trialExpired && (
            <div className='bg-amber-50 border border-amber-200 rounded-md p-4 mb-6'>
              <div className='flex items-start'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-5 w-5 text-amber-600 mr-3 mt-0.5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <div>
                  <h3 className='text-sm font-medium text-amber-800 mb-1'>
                    Trial Expired
                  </h3>
                  <p className='text-sm text-amber-700'>
                    Your 14-day free trial has ended. Please select a
                    subscription plan below to continue using all features.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Canceling status notification */}
          {userPlan?.accountStatus === 'canceling' && (
            <div className='bg-amber-50 border border-amber-200 rounded-md p-4 mb-6'>
              <div className='flex items-start'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-5 w-5 text-amber-600 mr-3 mt-0.5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <div>
                  <h3 className='text-sm font-medium text-amber-800 mb-1'>
                    {t('subscription.dialog.buttons.cancelingStatus')}
                  </h3>
                  <p className='text-sm text-amber-700'>
                    You'll have access to all features until{' '}
                    {userPlan?.accessUntil
                      ? new Date(userPlan.accessUntil).toLocaleDateString()
                      : 'the end of your current billing period'}
                    .
                  </p>
                </div>
              </div>
            </div>
          )}

          {paymentRejected && (
            <div className='bg-red-50 border border-red-200 rounded-md p-4 mb-6'>
              <div className='flex items-start'>
                <svg
                  className='h-5 w-5 text-red-500 mr-3 flex-shrink-0'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                    clipRule='evenodd'
                  />
                </svg>
                <div>
                  <h3 className='text-red-800 font-medium'>
                    {t('subscription.dialog.paymentFailedAlert.title')}
                  </h3>
                  <p className='text-red-700 mt-1'>
                    {daysSinceRejection >= 4
                      ? t(
                          'subscription.dialog.paymentFailedAlert.description.final'
                        )
                      : t(
                          'subscription.dialog.paymentFailedAlert.description.recent'
                        )}
                  </p>
                  <Button
                    className='mt-3 bg-red-600 hover:bg-red-700 text-white'
                    onClick={handleUpdateBillingInfo}
                  >
                    {t('subscription.dialog.paymentFailedAlert.updateButton')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <TiersAndPricing
            currentPlan={currentPlan}
            onPlanSelect={handleSubscribe}
            onManageSubscription={handleManageSubscription}
            isLoading={isLoading}
            trialExpired={trialExpired}
            hasActiveSubscription={!!subscription}
            scheduledPlanChange={scheduledPlanChange}
          />

          <div className='flex flex-wrap justify-center mt-4 gap-4'>
            <Button
              variant='outline'
              onClick={handleManageSubscription}
              disabled={isLoading}
            >
              {currentPlan === 'FREE_TRIAL' && !forceSubscription
                ? t('subscription.dialog.buttons.manageTrialSubscription')
                : t('subscription.dialog.buttons.manageSubscription')}
            </Button>

            {/* Add dedicated billing information update button */}
            <Button
              variant='outline'
              onClick={handleUpdateBillingInfo}
              disabled={isLoading}
              className='text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'
            >
              {t('subscription.dialog.buttons.updateBilling')}
            </Button>

            {/* Show Reactivate button for canceling/canceled status with integrated badge */}
            {userPlan?.accountStatus === 'canceling' ||
            userPlan?.accountStatus === 'canceled' ? (
              <Button
                variant='outline'
                onClick={handleReactivateAccount}
                disabled={isLoading}
                className='text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700 hover:border-green-300 pr-2 gap-2'
              >
                {t('subscription.dialog.buttons.reactivateAccount')}
                {/* <span className='inline-flex items-center text-xs rounded-full bg-amber-50 px-2 py-0.5 border border-amber-100 text-amber-600'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-3 w-3 mr-0.5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                  {daysRemaining !== null ? (
                    <>
                      {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
                    </>
                  ) : userPlan?.accountStatus === 'canceled' ? (
                    'Canceled'
                  ) : (
                    'Canceling'
                  )}
                </span> */}
              </Button>
            ) : (
              <Button
                variant='outline'
                onClick={handleCancelAccount}
                disabled={isLoading}
                className='text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600 hover:border-red-300'
              >
                {t('subscription.dialog.buttons.cancelAccount')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Account Cancellation */}
      <ConfirmationDialog
        open={showCancelConfirmation}
        onOpenChange={setShowCancelConfirmation}
        title={t('subscription.dialog.cancellation.title')}
        description={
          <>
            <p className='mb-4'>
              {t('subscription.dialog.cancellation.description')}
            </p>
            {currentPlan !== 'FREE_TRIAL' && (
              <div className='mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800'>
                <p className='font-medium'>
                  {t('subscription.dialog.cancellation.whatHappens')}
                </p>
                <ul className='list-disc pl-5 mt-2 text-sm'>
                  <li>{t('subscription.dialog.cancellation.details.0')}</li>
                  <li>{t('subscription.dialog.cancellation.details.1')}</li>
                  <li>{t('subscription.dialog.cancellation.details.2')}</li>
                  <li>{t('subscription.dialog.cancellation.details.3')}</li>
                </ul>
              </div>
            )}
            <p className='text-red-600 font-medium'>
              {t('subscription.dialog.cancellation.warning')}
            </p>
          </>
        }
        confirmText={t('subscription.dialog.cancellation.confirmButton')}
        cancelText={t('subscription.dialog.cancellation.keepButton')}
        confirmVariant='destructive'
        onConfirm={confirmCancelAccount}
        isLoading={isLoading}
      />
    </>
  );
}
